import { create } from 'zustand'
import { inboxApi, autoReplyRulesApi } from '@/lib/api'
import { toast } from 'sonner'
import type { InboxMessage, InboxStats, AutoReplyRule, CreateAutoReplyRuleData } from '@/types/senderEmail'

interface InboxState {
  messages: InboxMessage[]
  stats: InboxStats
  pendingApprovals: InboxMessage[]
  autoReplyRules: AutoReplyRule[]
  isLoading: boolean
  /** In flight for the overview cards specifically, kept apart from
   *  `isLoading` so the card row can show placeholders while the message list
   *  below it stays put. Changing the mailbox or the person refetches both, and
   *  swapping the whole list for a spinner on every filter change is what read
   *  as the screen blinking. */
  statsLoading: boolean

  // Pagination state for the "All messages" list. Held in the store so that
  // internal refetches (after reply/approve/reject/analyze) preserve the
  // current page, sentiment filter, and email search instead of snapping
  // back to page 0.
  page: number
  pageSize: number
  // Two independent classification filters (Reply Module spec §3): tone and
  // next action. Combined with AND server-side.
  sentiment?: string
  intent?: string
  search?: string
  // Exact contact address picked from the distinct-senders dropdown.
  fromAddress?: string
  // Teammate (platform user id) picked from the contact dropdown — keeps only
  // replies attributed to that person's campaigns in the shared mailbox.
  contactUserId?: number
  // When true, the list shows the shared "unassigned" bucket (mail attributed
  // to no one) instead of the default "my replies" view.
  unassigned?: boolean
  // When true, the list merges every teammate's mail this role may read into
  // one view (RBAC_Policy_Developer_Spec.docx §4-J "View replies: All" —
  // Owner/Admin/Manager). A no-op for Rep/Analyst: the backend's
  // readable_user_ids already collapses to just themselves for those roles.
  allMembers?: boolean
  // Selected mailbox (one of the current user's own sender rows). Scopes the
  // list to that shared mailbox's address, within which the per-user filter can
  // surface teammate replies. Undefined = the private cross-mailbox view.
  // Persisted so switching sentiment/search/page keeps the selected mailbox.
  senderEmailId?: number
  // Total rows matching the active filters, from the X-Total-Count header on
  // the list response. Null until the first fetch resolves.
  total: number | null

  // The two selectors, as the dropdowns hold them (a mailbox id as a string,
  // or the ALL_MAILBOXES sentinel; 'me' / 'all-members' / 'unassigned' / a
  // teammate id). They live here rather than in the page because the page
  // unmounts on every route change: reading a reply, opening the contact it
  // came from and coming back used to drop you on the default mailbox and
  // "My replies" again, losing a selection nobody asked to lose.
  //
  // Deliberately kept in memory, not localStorage: a mailbox id is only
  // meaningful for the account you are signed into, and reset() wipes these
  // with the rest on an account switch.
  mailboxValue: string
  personValue: string
  /** False until the page has resolved a mailbox and a person for this
   *  session. It is what tells boot whether it is opening the Inbox or
   *  returning to it. */
  selectionReady: boolean
  setSelection: (next: { mailboxValue?: string; personValue?: string }) => void

  fetchMessages: (params?: { sentiment?: string; intent?: string; search?: string; from_address?: string; contact_user_id?: number; unassigned?: boolean; all_members?: boolean; page?: number; campaign_id?: number; sender_email_id?: number }, opts?: { background?: boolean }) => Promise<void>
  fetchStats: () => Promise<void>
  fetchPendingApprovals: () => Promise<void>
  markAsRead: (id: number) => Promise<void>
  analyzeMessage: (id: number) => Promise<any>
  analyzeUnanalyzed: () => Promise<void>
  sendReply: (id: number, content: string) => Promise<any>
  approveReply: (id: number) => Promise<void>
  rejectReply: (id: number) => Promise<void>

  // Auto-reply rules
  fetchRules: () => Promise<void>
  createRule: (data: CreateAutoReplyRuleData) => Promise<AutoReplyRule>
  updateRule: (id: number, data: Partial<CreateAutoReplyRuleData & { is_active?: boolean }>) => Promise<void>
  deleteRule: (id: number) => Promise<void>

  // Reset — wipes per-user data on account switch (see main.tsx auth subscribe).
  reset: () => void
}

// Monotonic token for the messages list. Every fetchMessages call takes the
// next token; only the most recently issued call may commit its response to
// state. Older in-flight responses — from a previous filter, an overlapping
// debounced search, or the background analyze poll — are dropped so the list
// can never revert to a stale filter's results without a manual refresh.
// See AB#21833.
let latestFetchToken = 0

const initialState = {
  messages: [] as InboxMessage[],
  stats: { total: 0, unread: 0, matched: 0, pending_approval: 0, unanalyzed: 0, pending_analysis: 0, unassigned: 0, sentiment_breakdown: {}, sentiment_summary: {}, intent_breakdown: {} } as InboxStats,
  pendingApprovals: [] as InboxMessage[],
  autoReplyRules: [] as AutoReplyRule[],
  isLoading: false,
  statsLoading: false,
  page: 0,
  pageSize: 25,
  sentiment: undefined as string | undefined,
  intent: undefined as string | undefined,
  search: undefined as string | undefined,
  fromAddress: undefined as string | undefined,
  contactUserId: undefined as number | undefined,
  unassigned: false,
  allMembers: false,
  senderEmailId: undefined as number | undefined,
  total: null as number | null,
  mailboxValue: '',
  personValue: 'me',
  selectionReady: false,
}

export const useInboxStore = create<InboxState>()((set, get) => ({
  ...initialState,

  // Re-picking what is already selected must not notify subscribers: a
  // fresh state object re-renders the whole Inbox for no change.
  setSelection: (next) => set((s) => {
    const same = (Object.keys(next) as (keyof typeof next)[]).every((k) => s[k] === next[k])
    return same && s.selectionReady ? s : { ...next, selectionReady: true }
  }),

  fetchMessages: async (params, opts) => {
    const state = get()
    // Background refetches (e.g. the "Analyze all" poll loop) update the list
    // in place without flipping isLoading, so the list column never swaps to the
    // full-height spinner mid-poll — that swap is what read as screen "blinking".
    const background = opts?.background === true
    // A provided filter/page overrides persisted state; otherwise reuse the
    // current query so internal refetches stay on the same page and filters.
    // Changing a filter resets to the first page.
    const sentiment = params && 'sentiment' in params ? params.sentiment : state.sentiment
    const intent = params && 'intent' in params ? params.intent : state.intent
    const search = params && 'search' in params ? params.search : state.search
    const fromAddress = params && 'from_address' in params ? params.from_address : state.fromAddress
    const contactUserId = params && 'contact_user_id' in params ? params.contact_user_id : state.contactUserId
    const unassigned = params && 'unassigned' in params ? params.unassigned : state.unassigned
    const allMembers = params && 'all_members' in params ? params.all_members : state.allMembers
    const senderEmailId = params && 'sender_email_id' in params ? params.sender_email_id : state.senderEmailId
    const filterChanged =
      !!params &&
      ('sentiment' in params || 'intent' in params || 'search' in params || 'from_address' in params || 'contact_user_id' in params || 'unassigned' in params || 'all_members' in params || 'sender_email_id' in params)
    const page = params?.page ?? (filterChanged ? 0 : state.page)
    const { pageSize } = state

    // Claim this call's slot. Captured before the await so responses are
    // matched against the latest issued request, not the latest resolved one.
    const token = ++latestFetchToken

    set({ sentiment, intent, search, fromAddress, contactUserId, unassigned, allMembers, senderEmailId, page, ...(background ? {} : { isLoading: true }) })
    try {
      const response = await inboxApi.getAll({
        skip: page * pageSize,
        limit: pageSize,
        sentiment: sentiment || undefined,
        intent: intent || undefined,
        search: search || undefined,
        from_address: fromAddress || undefined,
        contact_user_id: contactUserId || undefined,
        unassigned: unassigned || undefined,
        all_members: allMembers || undefined,
        campaign_id: params?.campaign_id,
        sender_email_id: senderEmailId || undefined,
      })
      // A newer fetch superseded this one while it was in flight — drop the
      // stale response so it can't overwrite the current filter's results.
      // Leave isLoading alone: the newer request owns it.
      if (token !== latestFetchToken) return
      const totalHeader = response.headers['x-total-count']
      set({
        messages: response.data,
        total: totalHeader != null ? parseInt(totalHeader, 10) : null,
        // Always clear loading on the latest settled request (even a background
        // one), so a superseded foreground fetch can't leave the spinner stuck.
        isLoading: false,
      })
    } catch {
      if (token !== latestFetchToken) return
      set({ isLoading: false })
      toast.error('Failed to fetch inbox messages')
    }
  },

  fetchStats: async () => {
    // Scope the overview cards to the SAME mailbox + person filter as the list
    // so they always agree (previously stats ignored the selectors and always
    // showed the logged-in user's all-mailbox totals).
    const { senderEmailId, contactUserId, unassigned, allMembers } = get()
    set({ statsLoading: true })
    try {
      const response = await inboxApi.getStats({
        sender_email_id: senderEmailId || undefined,
        contact_user_id: contactUserId || undefined,
        unassigned: unassigned || undefined,
        all_members: allMembers || undefined,
      })
      set({ stats: response.data })
    } catch {
      // Silent, as before: the cards keep the last figures rather than
      // blanking. The flag still has to clear, or a single failed refresh
      // would leave placeholders on screen for the rest of the session.
    } finally {
      set({ statsLoading: false })
    }
  },

  fetchPendingApprovals: async () => {
    try {
      const response = await inboxApi.getPendingApprovals()
      set({ pendingApprovals: response.data })
    } catch {
      // silently fail
    }
  },

  markAsRead: async (id: number) => {
    const { messages, pendingApprovals, stats } = get()
    const target = messages.find(m => m.id === id) || pendingApprovals.find(m => m.id === id)
    if (!target || target.is_read) return

    const flip = (list: InboxMessage[]) =>
      list.map(m => (m.id === id ? { ...m, is_read: true } : m))
    set({
      messages: flip(messages),
      pendingApprovals: flip(pendingApprovals),
      stats: { ...stats, unread: Math.max(0, (stats.unread || 0) - 1) },
    })

    try {
      await inboxApi.markAsRead(id)
    } catch {
      // Revert on failure so the UI doesn't lie about server state.
      set({ messages, pendingApprovals, stats })
    }
  },

  analyzeMessage: async (id: number) => {
    try {
      const response = await inboxApi.analyze(id)
      await get().fetchMessages()
      await get().fetchStats()
      toast.success('Analysis completed')
      return response.data
    } catch {
      toast.error('Analysis failed')
      throw new Error('Analysis failed')
    }
  },

  analyzeUnanalyzed: async () => {
    let response
    try {
      response = await inboxApi.analyzeUnanalyzed()
    } catch {
      toast.error('Failed to queue analysis')
      return
    }

    const { queued, total_unanalyzed } = response.data
    if (queued === 0) {
      toast.success('Nothing to analyze. All matched replies are classified.')
      return
    }

    toast.success(`Queued ${queued} of ${total_unanalyzed} message(s) for classification`)

    // Poll until the matched-unanalyzed bucket drains. Resolves the action
    // promise only when polling stops, so the caller's loading state stays
    // active for the full duration.
    //
    // Stop conditions, in order:
    //   1. pending_analysis hits 0 -> success
    //   2. count plateaus for 4 consecutive ticks -> workers stalled / no LLM
    //   3. hard ceiling of 60 ticks (~3 min) -> give up, leave it running
    const INTERVAL_MS = 3000
    const MAX_ATTEMPTS = 60
    const STALL_LIMIT = 4

    return new Promise<void>((resolve) => {
      let attempts = 0
      let lastPending = Number.POSITIVE_INFINITY
      let stallCount = 0

      const tick = async () => {
        attempts += 1
        await get().fetchStats()
        // Silent refetch — keep the current list visible and just update
        // sentiment badges as classifications land, instead of swapping the
        // whole list for a spinner on every tick.
        await get().fetchMessages(undefined, { background: true })
        const pending = get().stats.pending_analysis ?? 0

        if (pending === 0) {
          toast.success('Sentiment analysis complete')
          return resolve()
        }
        if (pending >= lastPending) {
          stallCount += 1
        } else {
          stallCount = 0
        }
        lastPending = pending

        if (stallCount >= STALL_LIMIT) {
          toast.message(`Analysis stalled: ${pending} message(s) pending. Check worker logs.`)
          return resolve()
        }
        if (attempts >= MAX_ATTEMPTS) {
          toast.message(`${pending} message(s) still pending. Continuing in background.`)
          return resolve()
        }
        setTimeout(tick, INTERVAL_MS)
      }

      setTimeout(tick, INTERVAL_MS)
    })
  },

  sendReply: async (id: number, content: string) => {
    try {
      const response = await inboxApi.reply(id, { content })
      await get().fetchMessages()
      toast.success('Reply sent')
      return response.data
    } catch {
      toast.error('Failed to send reply')
      throw new Error('Failed to send reply')
    }
  },

  approveReply: async (id: number) => {
    try {
      await inboxApi.approveReply(id)
      await get().fetchMessages()
      await get().fetchPendingApprovals()
      toast.success('Auto-reply approved and sent')
    } catch {
      toast.error('Failed to approve reply')
    }
  },

  rejectReply: async (id: number) => {
    try {
      await inboxApi.rejectReply(id)
      await get().fetchMessages()
      await get().fetchPendingApprovals()
      toast.success('Auto-reply rejected')
    } catch {
      toast.error('Failed to reject reply')
    }
  },

  fetchRules: async () => {
    try {
      const response = await autoReplyRulesApi.getAll()
      set({ autoReplyRules: response.data })
    } catch {
      toast.error('Failed to fetch auto-reply rules')
    }
  },

  createRule: async (data: CreateAutoReplyRuleData) => {
    try {
      const response = await autoReplyRulesApi.create(data)
      const { autoReplyRules } = get()
      set({ autoReplyRules: [response.data, ...autoReplyRules] })
      toast.success('Auto-reply rule created')
      return response.data
    } catch {
      toast.error('Failed to create rule')
      throw new Error('Failed to create rule')
    }
  },

  updateRule: async (id: number, data) => {
    try {
      const response = await autoReplyRulesApi.update(id, data)
      const { autoReplyRules } = get()
      set({ autoReplyRules: autoReplyRules.map(r => r.id === id ? response.data : r) })
      toast.success('Rule updated')
    } catch {
      toast.error('Failed to update rule')
    }
  },

  deleteRule: async (id: number) => {
    try {
      await autoReplyRulesApi.delete(id)
      const { autoReplyRules } = get()
      set({ autoReplyRules: autoReplyRules.filter(r => r.id !== id) })
      toast.success('Rule deleted')
    } catch {
      toast.error('Failed to delete rule')
    }
  },

  // Bump the token so any fetch in flight from the previous account is dropped
  // instead of committing its rows into the freshly-cleared state.
  reset: () => { latestFetchToken++; set(initialState) },
}))
